/**
 * Turn an axios/DRF failure into something a human can act on.
 *
 * Every CRM write endpoint answers with one of a handful of shapes:
 *
 *   "Something went wrong"                       (bare string body)
 *   { detail: "Fill the disposition first." }    (DRF APIException)
 *   { non_field_errors: ["..."] }                (serializer validate())
 *   { requested_amount: ["Must be positive."] }  (per-field errors)
 *
 * Every one of those carries the *reason* the server refused — which is
 * exactly what the operator needs when a permission or validation rule
 * blocks them. Swallowing it and showing "Could not save" turns a rule
 * into a mystery, so prefer this over a hard-coded fallback string.
 */

const FIELD_LABELS: Record<string, string> = {
  non_field_errors: '',
  detail: '',
  status: 'Status',
  priority: 'Priority',
  resolution_note: 'Resolution note',
  reason_code_id: 'Reason',
  sub_reason_code_id: 'Sub-reason',
  is_avoidable: 'Avoidability',
  sla_disposition_notes: 'Disposition notes',
  requested_amount: 'Requested amount',
  approved_amount: 'Approved amount',
  items: 'Items',
  user_ids: 'Assignees',
  primary: 'Primary owner',
  order_id: 'Order',
  product_id: 'Product',
  quantity: 'Quantity',
};

function firstString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = firstString(entry);
      if (found) return found;
    }
  }
  if (value && typeof value === 'object') {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      const found = firstString(entry);
      if (found) return found;
    }
  }
  return null;
}

/** The server's explanation, or `null` when it did not give one. */
export function apiErrorDetail(err: unknown): string | null {
  const response = (err as { response?: { data?: unknown; status?: number } }).response;
  const data = response?.data;

  if (typeof data === 'string') {
    // HTML error pages are noise, not an explanation.
    return data.trim().startsWith('<') ? null : firstString(data);
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const direct = firstString(record.detail) ?? firstString(record.non_field_errors);
    if (direct) return direct;

    for (const [field, value] of Object.entries(record)) {
      const message = firstString(value);
      if (!message) continue;
      const label = FIELD_LABELS[field] ?? humanizeField(field);
      return label ? `${label}: ${message}` : message;
    }
  }

  return null;
}

/**
 * The server's explanation, falling back to `fallback` (and adding a hint
 * about what the status code usually means, since a bare "Forbidden" tells
 * an operator nothing about who *can* do the thing).
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = apiErrorDetail(err);
  if (detail) return detail;

  const status = (err as { response?: { status?: number } }).response?.status;
  if (status === 403) return `${fallback} Your role does not allow this action.`;
  if (status === 404) return `${fallback} It may have been deleted — reload the page.`;
  if (status === 409) return `${fallback} Someone else changed it first — reload the page.`;
  if (status != null && status >= 500) return `${fallback} The server errored (${status}).`;
  return fallback;
}

function humanizeField(field: string): string {
  const cleaned = field.replace(/_id$/, '').replace(/_/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
